import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import TasksTable from '../../components/tasks/TasksTable.jsx';
import MissionsTable from '../../components/tasks/MissionsTable.jsx';
import TaskDetailsModal from '../../components/tasks/TaskDetailsModal.jsx';
import ConfirmationModal from '../../components/common/ConfirmationModal.jsx';
import { groupMissions, getMissionStats } from '../../utils/group-missions.js';

function teamsMatch(a, b) {
    const x = (a ?? '').toString().trim().toLowerCase();
    const y = (b ?? '').toString().trim().toLowerCase();
    return x.length > 0 && x === y;
}

const StatsDashboard = ({
    viewType,
    teamId,
    quarter,
    weekRange,
    weekLabel,
    allTasks,
    loading,
    hasManagementRights,
    isItLeadership,
    currentUserEmail,
    currentUserTeam,
    refreshTasks,
    showTasks,
    onTaskStatusUpdated
}) => {
    const [selectedTask, setSelectedTask] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [taskToComplete, setTaskToComplete] = useState(null);

    const handleRowClick = (task) => {
        if (task.parent_task_id) {
            // Subtask clicked — open the parent's details modal
            const parent = allTasks?.find(
                (t) => t.task_id === task.parent_task_id
            );
            setSelectedTask(parent || task);
        } else {
            setSelectedTask(task);
        }
    };

    const handleMarkComplete = (task) => {
        setTaskToComplete(task);
        setIsConfirmModalOpen(true);
    };

    const confirmCompleteTask = async () => {
        if (!taskToComplete) return;
        try {
            const response = await callOysterApi(
                `/users/${encodeURIComponent(
                    taskToComplete.assignee_email
                )}/todos/${encodeURIComponent(taskToComplete.id)}/status`,
                'PATCH',
                { status: 'completed' }
            );
            if (response.ok) {
                toast.success('Marked as complete!');
                if (onTaskStatusUpdated) {
                    onTaskStatusUpdated(taskToComplete.id, 'completed');
                }
                if (refreshTasks) refreshTasks();
                if (selectedTask && selectedTask.id === taskToComplete.id) {
                    setSelectedTask({ ...selectedTask, status: 'completed' });
                }
            } else {
                toast.error('Failed to update');
            }
        } catch (error) {
            console.error('Error updating task:', error);
        } finally {
            setTaskToComplete(null);
            setIsConfirmModalOpen(false);
        }
    };

    const isInSelectedWeek = useMemo(() => {
        if (!weekRange) return () => true;
        const start = Date.parse(`${weekRange.start}T00:00:00Z`);
        const end = Date.parse(`${weekRange.end}T23:59:59Z`);

        const inRange = (value) => {
            if (!value) return false;
            const parsed = Date.parse(`${value}T00:00:00Z`);
            return !Number.isNaN(parsed) && parsed >= start && parsed <= end;
        };

        return (task) => {
            const completedDate =
                task.completed_date ||
                (task.completed_at ? task.completed_at.split('T')[0] : null);
            return inRange(task.due_date) || inRange(completedDate);
        };
    }, [weekRange]);

    const filteredTasks = useMemo(() => {
        if (!allTasks) return [];
        if (viewType === 'team' && teamId) {
            return allTasks.filter((t) => teamsMatch(t.team_id, teamId));
        }
        return allTasks;
    }, [allTasks, viewType, teamId]);

    const visibleTasks = useMemo(() => {
        if (!weekRange) return filteredTasks;
        return filteredTasks.filter(isInSelectedWeek);
    }, [filteredTasks, weekRange, isInSelectedWeek]);

    const quarterContextTasks = useMemo(() => {
        if (!weekRange) return [];
        return filteredTasks.filter((t) => !isInSelectedWeek(t));
    }, [filteredTasks, weekRange, isInSelectedWeek]);

    // Separate subtasks from top-level items
    const topLevel = useMemo(
        () => visibleTasks.filter((t) => !t.parent_task_id),
        [visibleTasks]
    );
    const subtaskMap = useMemo(() => {
        const map = new Map();
        visibleTasks
            .filter((t) => t.parent_task_id)
            .forEach((t) => {
                const existing = map.get(t.parent_task_id) || [];
                existing.push(t);
                map.set(t.parent_task_id, existing);
            });
        return map;
    }, [visibleTasks]);

    const quarterContextTopLevel = useMemo(
        () => quarterContextTasks.filter((t) => !t.parent_task_id),
        [quarterContextTasks]
    );
    const quarterContextSubtaskMap = useMemo(() => {
        const map = new Map();
        quarterContextTasks
            .filter((t) => t.parent_task_id)
            .forEach((t) => {
                const existing = map.get(t.parent_task_id) || [];
                existing.push(t);
                map.set(t.parent_task_id, existing);
            });
        return map;
    }, [quarterContextTasks]);

    // Split top-level by item_type
    const rocks = useMemo(
        () => topLevel.filter((t) => t.item_type === 'rock'),
        [topLevel]
    );
    const tasks = useMemo(
        () => topLevel.filter((t) => t.item_type !== 'rock'),
        [topLevel]
    );
    const quarterContextRocks = useMemo(
        () => quarterContextTopLevel.filter((t) => t.item_type === 'rock'),
        [quarterContextTopLevel]
    );
    const quarterContextTasksOnly = useMemo(
        () => quarterContextTopLevel.filter((t) => t.item_type !== 'rock'),
        [quarterContextTopLevel]
    );

    const getStats = (items) => ({
        total: items.length,
        completed: items.filter((t) => t.status === 'completed').length,
        pending: items.filter((t) => t.status === 'needsAction').length,
        overdue: items.filter(
            (t) => t.status === 'needsAction' && t.sla?.is_overdue
        ).length
    });

    // Mission-First: collapse per-assignee rock rows into one entry per mission
    const missionGroups = useMemo(() => groupMissions(rocks), [rocks]);
    const quarterContextMissionGroups = useMemo(
        () => groupMissions(quarterContextRocks),
        [quarterContextRocks]
    );

    const rockStats = useMemo(
        () => getMissionStats(missionGroups),
        [missionGroups]
    );
    const taskStats = useMemo(() => getStats(tasks), [tasks]);

    if (loading) return <LoadingSpinner />;

    const renderStatsRow = (stats, label, emoji) => (
        <div>
            <h3 className="section-label">
                {emoji} {label}
            </h3>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-title">Total</div>
                    <div className="stat-value">{stats.total}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Pending</div>
                    <div className="stat-value">{stats.pending}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Completed</div>
                    <div className="stat-value text-green">
                        {stats.completed}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Overdue</div>
                    <div className="stat-value text-red">{stats.overdue}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="stats-dashboard-view">
            {/* Completion Confirmation Modal */}
            {isConfirmModalOpen && (
                <ConfirmationModal
                    title="Complete Item"
                    message={`Are you sure you want to mark "${taskToComplete?.title}" as complete?`}
                    onConfirm={confirmCompleteTask}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Task Details Modal */}
            {selectedTask && (
                <TaskDetailsModal
                    task={selectedTask}
                    subtasks={subtaskMap.get(selectedTask.task_id) || []}
                    onClose={() => setSelectedTask(null)}
                    onSubtasksChanged={refreshTasks}
                    onTaskStatusUpdated={onTaskStatusUpdated}
                    currentUserEmail={currentUserEmail}
                    currentUserTeam={currentUserTeam}
                    isItLeadership={isItLeadership}
                    hasManagementRights={hasManagementRights}
                />
            )}

            <h2 className="view-subtitle">
                Overview - {quarter}
                {weekRange ? ` ${weekLabel}` : ''}
            </h2>

            {renderStatsRow(rockStats, 'Missions')}
            <MissionsTable
                missions={missionGroups}
                emptyMessage={`No missions found for ${
                    viewType === 'team' ? teamId : 'All Teams'
                } in ${quarter}.`}
                onRowClick={handleRowClick}
                onCompleteAction={handleMarkComplete}
                currentUserEmail={currentUserEmail}
                currentUserTeam={currentUserTeam}
                isItLeadership={isItLeadership}
                hasManagementRights={hasManagementRights}
            />

            <div style={{ marginTop: '2rem' }} />

            {showTasks && (
                <>
                    {renderStatsRow(taskStats, 'Tasks')}
                    <TasksTable
                        items={tasks}
                        subtaskMap={subtaskMap}
                        label="Task"
                        emptyMessage={`No tasks found for ${
                            viewType === 'team' ? teamId : 'All Teams'
                        } in ${quarter}.`}
                        onRowClick={handleRowClick}
                        onCompleteAction={handleMarkComplete}
                        currentUserEmail={currentUserEmail}
                        currentUserTeam={currentUserTeam}
                        isItLeadership={isItLeadership}
                        hasManagementRights={hasManagementRights}
                    />
                </>
            )}

            {weekRange && (viewType === 'all' || viewType === 'team') && (
                <>
                    <div style={{ marginTop: '2rem' }} />
                    <h3 className="section-label">
                        Quarter Context (Outside {weekLabel})
                    </h3>
                    <MissionsTable
                        missions={quarterContextMissionGroups}
                        emptyMessage={`No quarter context missions outside ${weekLabel}.`}
                        onRowClick={handleRowClick}
                        onCompleteAction={handleMarkComplete}
                        currentUserEmail={currentUserEmail}
                        currentUserTeam={currentUserTeam}
                        isItLeadership={isItLeadership}
                        hasManagementRights={hasManagementRights}
                    />
                    {showTasks && (
                        <TasksTable
                            items={quarterContextTasksOnly}
                            subtaskMap={quarterContextSubtaskMap}
                            label="Task"
                            emptyMessage={`No quarter context tasks outside ${weekLabel}.`}
                            onRowClick={handleRowClick}
                            onCompleteAction={handleMarkComplete}
                            currentUserEmail={currentUserEmail}
                            currentUserTeam={currentUserTeam}
                            isItLeadership={isItLeadership}
                            hasManagementRights={hasManagementRights}
                        />
                    )}
                </>
            )}
        </div>
    );
};

StatsDashboard.propTypes = {
    viewType: PropTypes.string.isRequired,
    teamId: PropTypes.string,
    quarter: PropTypes.string.isRequired,
    weekRange: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired
    }),
    weekLabel: PropTypes.string,
    allTasks: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    hasManagementRights: PropTypes.bool,
    isItLeadership: PropTypes.bool,
    currentUserEmail: PropTypes.string,
    currentUserTeam: PropTypes.string,
    refreshTasks: PropTypes.func,
    showTasks: PropTypes.bool,
    onTaskStatusUpdated: PropTypes.func
};

export default StatsDashboard;
